/**
 * Singleton process reactions (V2).
 * One place: if blocker finished → continue / stop the waiting process.
 * Called from redux middleware after the reducer runs.
 */
import { f7 } from 'framework7-react';
import {
  SET_USER_CREDENTIALS_TASK,
  SET_CREATE_TEMPLATE_REQUEST_TASK,
  RECEIVE_LIST_REQUESTED_DOCUMENTS,
  RECEIVE_ERROR_ON_PREPARE_FROM_TEMPLATE,
  clearUserCredentialsTask,
  clearCreateTemplateRequestTask,
  cancelCreateTemplateRequest,
  setCreateTemplateRequestTask,
  startSendRequest,
  stopSendRequest,
  prepareTemplateRequest
} from './actions/rootAction';
import {
  createCreateTemplateRequestTask,
  CREATE_TEMPLATE_REQUEST_STATUS,
  isCreateTemplateAwaitingCredentials,
  isCreateTemplatePreparing,
  isCreateTemplateRequestCancelled
} from './createTemplateRequestTask';
import {
  isUserCredentialsCancelled,
  isUserCredentialsDone
} from './userCredentialsTask';
import { isSendAwaitingTemplate } from './sendRequestTask';
import { gaeApp } from '../components/helpers/Analytics';
import PaperflyAPI from '../server/api/PaperflyAPI';
import II18 from '../components/translate/II18';

export function reactToRequestProcessAction(action, getState, dispatch) {
  switch (action.type) {
    case SET_USER_CREDENTIALS_TASK:
      onCredentialsTaskSet(getState(), dispatch);
      break;

    case SET_CREATE_TEMPLATE_REQUEST_TASK:
      onCreateTemplateTaskSet(action.payload, getState(), dispatch);
      break;

    case RECEIVE_LIST_REQUESTED_DOCUMENTS:
      if (getState().templateRequestPrepared) {
        onTemplatePrepared(getState(), dispatch);
      }
      break;

    case RECEIVE_ERROR_ON_PREPARE_FROM_TEMPLATE:
      onTemplatePrepareFailed(getState(), dispatch);
      break;

    // no default
  }
}

/** Credentials done / cancelled → continue or stop prepare */
function onCredentialsTaskSet(state, dispatch) {
  const credentialsTask = state.userCredentialsTask;
  const prepareTask = state.createTemplateRequestTask;

  if (
    isUserCredentialsDone(credentialsTask) &&
    isCreateTemplateAwaitingCredentials(prepareTask)
  ) {
    gaeApp('RESUME_CREATE_TEMPLATE_REQUEST_AFTER_CREDENTIALS');
    dispatch(clearUserCredentialsTask());
    dispatch(
      setCreateTemplateRequestTask(
        createCreateTemplateRequestTask({
          status: CREATE_TEMPLATE_REQUEST_STATUS.PREPARING,
          usersData: state.usersData,
          skipPreparingDialog: prepareTask.skipPreparingDialog
        })
      )
    );
    return;
  }

  if (
    isUserCredentialsCancelled(credentialsTask) &&
    isCreateTemplateAwaitingCredentials(prepareTask)
  ) {
    gaeApp('USER_CREDENTIALS_CANCELLED', 'create_template');
    dispatch(clearUserCredentialsTask());
    dispatch(cancelCreateTemplateRequest());
  }
}

/** Prepare → preparing: run API. Prepare cancelled → stop send if waiting. */
function onCreateTemplateTaskSet(prepareTask, state, dispatch) {
  if (
    isCreateTemplatePreparing(prepareTask) &&
    !state.templateRequestPrepared
  ) {
    gaeApp('PREPARE_TEMPLATE_REQUEST');
    dispatch(
      prepareTemplateRequest(state.recipientRequestId, prepareTask.usersData, {
        skipPreparingDialog: prepareTask.skipPreparingDialog
      })
    );
    return;
  }

  if (
    isCreateTemplateRequestCancelled(prepareTask) &&
    isSendAwaitingTemplate(state.sendRequestTask)
  ) {
    gaeApp('CREATE_TEMPLATE_REQUEST_CANCELLED', 'send');
    dispatch(clearCreateTemplateRequestTask());
    dispatch(stopSendRequest());
  }
}

/** Prepare done → continue send if waiting */
function onTemplatePrepared(state, dispatch) {
  gaeApp('TEMPLATE_REQUEST_PREPARED');
  dispatch(clearCreateTemplateRequestTask());

  const sendTask = state.sendRequestTask;
  if (!isSendAwaitingTemplate(sendTask)) {
    return;
  }

  gaeApp('SEND_REQUEST.BEGIN', 'after_template_prepared');
  dispatch(
    startSendRequest({
      fromDocView: sendTask.fromDocView,
      tillPage: sendTask.tillPage,
      noProgressDialog: sendTask.noProgressDialog,
      skipThankyou: sendTask.skipThankyou
    })
  );
}

/** Prepare failed → stop send if waiting */
function onTemplatePrepareFailed(state, dispatch) {
  gaeApp('ERROR_ON_PREPARE_TEMPLATE_REQUEST');
  PaperflyAPI.submitErrorProtocol(
    state.recipientRequestId,
    'ERROR_ON_PREPARE_TEMPLATE_REQUEST',
    state.templateRequestPreparedError
  );
  dispatch(clearCreateTemplateRequestTask());

  if (!isSendAwaitingTemplate(state.sendRequestTask)) {
    return;
  }

  gaeApp('ERROR_ON_PREPARE_TEMPLATE_REQUEST', 'send');
  f7.dialog.alert(
    II18.get('dialog_error_on_send', state.templateRequestPreparedError),
    II18.get('common_error')
  );
  dispatch(stopSendRequest());
}
